export type PhotoPreviewResult = {
  dataUrl?: string;
  file?: File;
  error?: string;
};

export class ManagementPhotoPreviewService {
  private readonly maxFileSize = 5.05 * 1024 * 1024;
  private readonly maxWidth = 960;
  private readonly maxHeight = 540;

  constructor(private document: Document) {}

  createPreview(file: File | undefined): Promise<PhotoPreviewResult> {
    if (!file || !file.type.startsWith('image/')) {
      return Promise.resolve({ error: 'Please select an image file.' });
    }

    if (file.size > this.maxFileSize) {
      return Promise.resolve({ error: 'The selected photo is too large. Please choose an image 5.00 MB or smaller.' });
    }

    return new Promise(resolve => {
      const imageUrl = URL.createObjectURL(file);
      const image = this.document.createElement('img');
      image.onload = () => {
        const scale = Math.min(1, this.maxWidth / image.naturalWidth, this.maxHeight / image.naturalHeight);
        const canvas = this.document.createElement('canvas');
        canvas.width = Math.max(Math.round(image.naturalWidth * scale), 1);
        canvas.height = Math.max(Math.round(image.naturalHeight * scale), 1);
        const context = canvas.getContext('2d');

        if (!context) {
          URL.revokeObjectURL(imageUrl);
          resolve({ error: 'Unable to process this image.' });
          return;
        }

        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.78);
        URL.revokeObjectURL(imageUrl);
        canvas.toBlob(blob => {
          if (!blob) {
            resolve({ error: 'Unable to process this image.' });
            return;
          }

          const filename = this.toJpegFilename(file.name);
          resolve({
            dataUrl,
            file: new File([blob], filename, { type: 'image/jpeg' })
          });
        }, 'image/jpeg', 0.78);
      };
      image.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        resolve({ error: 'Unable to process this image.' });
      };
      image.src = imageUrl;
    });
  }

  private toJpegFilename(filename: string): string {
    const normalizedName = filename.trim() || 'cover-photo';
    return normalizedName.replace(/\.[^.]+$/, '') + '.jpg';
  }
}
